import firebase from "config/firebase";
import { toast } from "react-toastify";

const PAGE_SIZE = 10;

// Helper: chunk array into groups of n
const chunkArray = (arr, n) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += n) {
    chunks.push(arr.slice(i, i + n));
  }
  return chunks;
};

// Batch fetch user profiles by IDs
const batchFetchUsers = async (ids) => {
  if (!ids || ids.length === 0) return {};
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  const chunks = chunkArray(uniqueIds, 10);
  let userMap = {};
  for (const chunk of chunks) {
    const snapshot = await firebase
      .firestore()
      .collection("users")
      .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
      .get();
    snapshot.docs.forEach((doc) => {
      userMap[doc.id] = { id: doc.id, ...doc.data() };
    });
  }
  return userMap;
};

// Enrich raw transaction docs with user profiles
const enrichTransactions = async (docs) => {
  const transactions = docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  const athleteIds = transactions.map((t) => t.athleteId).filter(Boolean);
  const fanIds = transactions.map((t) => t.userId).filter(Boolean);
  const allIds = [...new Set([...athleteIds, ...fanIds])];
  const userMap = await batchFetchUsers(allIds);
  return transactions.map((t) => ({
    ...t,
    athleteProfile: userMap[t.athleteId] || null,
    fanProfile: userMap[t.userId] || null,
  }));
};

// Initial fetch — first 10 from Firestore
export const fetchBoostTransactions =
  (onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch({ type: "BOOST_TRANSACTIONS_LOADING" });
    try {
      const snapshot = await firebase
        .firestore()
        .collection("boost_transactions")
        .orderBy("createdAt", "desc")
        .limit(PAGE_SIZE)
        .get();

      const enriched = await enrichTransactions(snapshot.docs);
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      const hasMore = snapshot.docs.length === PAGE_SIZE;

      dispatch({
        type: "SET_BOOST_TRANSACTIONS",
        payload: { transactions: enriched, lastDoc, hasMore },
      });
      onSuccess();
    } catch (error) {
      console.error("Error fetching boost transactions:", error);
      toast.error("Failed to load transactions. Please try again.");
      dispatch({ type: "BOOST_TRANSACTIONS_ERROR", payload: error.message });
    }
  };

// Load more — fetch next 10 starting after lastDoc cursor
export const loadMoreBoostTransactions =
  (lastDoc, onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch({ type: "BOOST_LOAD_MORE_LOADING" });
    try {
      const snapshot = await firebase
        .firestore()
        .collection("boost_transactions")
        .orderBy("createdAt", "desc")
        .startAfter(lastDoc)
        .limit(PAGE_SIZE)
        .get();

      const enriched = await enrichTransactions(snapshot.docs);
      const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || lastDoc;
      const hasMore = snapshot.docs.length === PAGE_SIZE;

      dispatch({
        type: "APPEND_BOOST_TRANSACTIONS",
        payload: { transactions: enriched, lastDoc: newLastDoc, hasMore },
      });
      onSuccess();
    } catch (error) {
      console.error("Error loading more transactions:", error);
      toast.error("Failed to load more transactions.");
      dispatch({ type: "BOOST_TRANSACTIONS_ERROR", payload: error.message });
    }
  };

// Fetch revenue summary metrics
export const fetchBoostMetrics =
  (onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch({ type: "BOOST_METRICS_LOADING" });
    try {
      const txSnapshot = await firebase
        .firestore()
        .collection("boost_transactions")
        .get();

      const totalTransactions = txSnapshot.size;
      const totalRevenue = txSnapshot.docs.reduce(
        (sum, doc) => sum + (doc.data().totalAmount || 0),
        0
      );
      const averageTransaction =
        totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

      const now = firebase.firestore.Timestamp.now();
      const activeSnapshot = await firebase
        .firestore()
        .collection("athlete_boosts")
        .where("expiryTime", ">", now)
        .get();
      const activeBoosts = activeSnapshot.size;

      dispatch({
        type: "SET_BOOST_METRICS",
        payload: { totalRevenue, totalTransactions, activeBoosts, averageTransaction },
      });
      onSuccess();
    } catch (error) {
      console.error("Error fetching boost metrics:", error);
      toast.error("Failed to load metrics.");
      dispatch({ type: "BOOST_METRICS_ERROR", payload: error.message });
    }
  };

// Fetch single transaction details
export const fetchTransactionDetails =
  (paymentId, onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch({ type: "BOOST_DETAIL_LOADING" });
    try {
      const txDoc = await firebase
        .firestore()
        .collection("boost_transactions")
        .doc(paymentId)
        .get();

      if (!txDoc.exists) {
        toast.error("Transaction not found.");
        dispatch({ type: "BOOST_DETAIL_ERROR", payload: "Not found" });
        return;
      }

      const transaction = { id: txDoc.id, ...txDoc.data() };

      let athleteBoostData = null;
      if (transaction.athleteId) {
        const boostDoc = await firebase
          .firestore()
          .collection("athlete_boosts")
          .doc(transaction.athleteId)
          .get();
        if (boostDoc.exists) {
          athleteBoostData = { id: boostDoc.id, ...boostDoc.data() };
        }
      }

      const ids = [transaction.athleteId, transaction.userId].filter(Boolean);
      const userMap = await batchFetchUsers(ids);

      dispatch({
        type: "SET_BOOST_SELECTED_TRANSACTION",
        payload: {
          ...transaction,
          athleteProfile: userMap[transaction.athleteId] || null,
          fanProfile: userMap[transaction.userId] || null,
          athleteBoostData,
        },
      });
      onSuccess();
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      toast.error("Failed to load transaction details.");
      dispatch({ type: "BOOST_DETAIL_ERROR", payload: error.message });
    }
  };

// Clear selected transaction
export const clearSelectedTransaction = () => ({
  type: "CLEAR_BOOST_SELECTED_TRANSACTION",
});
